function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { DmpAuthorizationStatus, InsOid } from "cnam/types/types";
import { CnamApiCpsCardBlocked, CnamApiCpsCardError, CnamApiCpsCardWrongCode, CnamApiDmpAuthorizationDenied, CnamApiDmpStatusClosed, CnamApiDmpStatusNotFound, CnamApiError, CnamApiInsError, CnamApiVitaleCardError } from "cnam/utils/cnam-api-errors";
import { Feature } from "core/services/features";
import { isDevelopmentOrLocal } from "core/utils/runtime-environment";
import { getItemFromLocalStorage, removeItemFromLocalStorage, setItemInLocalStorage } from "core/utils/use-local-storage";
import { DmpAdapterName } from "../cnam-manager-factory";
import DMPConnectHttpInstance from "./dmp-connect-http-instance";
/**
 * The minimum interface to DmpConnect-JS.
 *
 * This API is on a local server, accessible through a WebSocket on
 * https://localhost.icanopee.net:9980 (v1) or 9982 (v2).
 *
 * The DMPConnectJS API is asynchronous: a command is launched, then at some
 * point a result is received through the socket.
 * The DMPConnectJS is not based on Promises, this Service transforms some calls
 * as Promises.
 *
 * Code is greatly inspired from the examples.js of the DMPConnectJS SDK.
 */

export default class DmpConnect {
  constructor(pinCodeManager, medic, accessToken, featuresService) {
    _defineProperty(this, "transactionsTimeoutInSeconds", 60);
    _defineProperty(this, "cachedNetworkParams", void 0);
    this.pinCodeManager = pinCodeManager;
    this.medic = medic;
    this.accessToken = accessToken;
    this.featuresService = featuresService;
    this.instance = medic.dmpAdapterName == DmpAdapterName.DMPConnectJsOlaqin ? new DMPConnectHttpInstance(accessToken) : new DMPConnect((/*message*/
    ) => {
      // This callback can help debug DMPConnectJS.
      // It displays a log of log coming from the client.
      // console.log(message);
    }, () => {
      // Called when internal state has been updated.
    }, (error, _command, _timeoutInSeconds, answerCallback) => {
      // Called when an error happens
      // console.error(
      //  `Error in dmpconnect.js when running ${command.s_commandName}`,
      //  error
      // );
      answerCallback({
        error
      });
    }, null, null, "localhost.icanopee.net");
  }
  static get isDevelopmentMode() {
    return isDevelopmentOrLocal();
  }
  get isRemoteMode() {
    return this.instance instanceof DMPConnectHttpInstance;
  }
  get isDevelopmentMode() {
    return DmpConnect.isDevelopmentMode || Boolean(this.featuresService?.isEnabled(Feature.DmpConnectIsDevelopmentMode));
  }
  get connectionParams() {
    if (this.isDevelopmentMode) {
      // Homologué server
      return {
        tlsiServerFullUrl: "https://qualiflps.services-ps.ameli.fr",
        dmpServerName: "lps.formation1"
      };

      // Development server
      // return {
      //   tlsiServerFullUrl: "https://qualiflps.services-ps.ameli.fr",
      //   dmpServerName: "formation1"
      // };
    }
    return {
      tlsiServerFullUrl: "https://services-ps.ameli.fr/lps",
      dmpServerName: ""
    };
  }
  async sessionOpened() {
    return new Promise(async resolve => {
      this.instance.hl_sessionExists({}, function (result) {
        resolve(result.i_exists == 1);
      });
    });
  }
  windowsUserForMedic(medic) {
    return medic ? getItemFromLocalStorage(this._windowsUserForMedicKey(medic)) : undefined;
  }
  setWindowsUserForMedic(medic, value) {
    const key = this._windowsUserForMedicKey(medic);
    if (!value) {
      removeItemFromLocalStorage(key);
    } else {
      setItemInLocalStorage(key, value);
    }
  }
  _windowsUserForMedicKey(medic) {
    return `service:dmp-connect:medic-${medic.id}:windows-user`;
  }
  selectedHealthCentreForMedic(medic) {
    const selectedHealthCentreForMedicFromLocalStorage = getItemFromLocalStorage(this._selectedHealthCentreKey(medic));
    return selectedHealthCentreForMedicFromLocalStorage == null ? undefined : selectedHealthCentreForMedicFromLocalStorage;
  }
  setSelectedHealthCentreForMedic(medic, value) {
    return setItemInLocalStorage(this._selectedHealthCentreKey(medic), value);
  }
  _selectedHealthCentreKey(medic) {
    return `service:dmp-connect:medic-${medic.id}:selected-health-centre`;
  }

  /**
   * Load and register the DmpConnect configuration.
   */
  async registerConfiguration() {
    const configuration = await this._downloadConfiguration();
    await this._setServerConfiguration(configuration);
  }

  /**
   * Base64 string containing a cached version of the DmpConnect configuration.
   * The configuration is stored on the server as a static file and downloaded.
   */

  _configurationFilename() {
    if (this.isDevelopmentMode) {
      return "dcparameters.dmpcjs2.dev.manual.dat_semeiajs2.txt";
    }
    return "dcparameters.dmpcjs2.prod.manual.dat_semeiajs2.txt";
  }

  /**
   * Download or read configuration from the cache.
   */
  async _downloadConfiguration() {
    return new Promise(async resolve => {
      if (this._cachedDcParameters) {
        resolve(this._cachedDcParameters);
        return;
      }
      const response = await fetch(`/assets/dmpconnectjs/configs/${this._configurationFilename()}`);
      this._cachedDcParameters = await response.text();
      resolve(this._cachedDcParameters);
    });
  }
  async _setServerConfiguration(configuration) {
    const params = {
      s_dcparameters64: configuration
    };

    // Check if registration is already done
    const alreadyRegistered = await new Promise(async (resolve, reject) => {
      this.instance.sendHttpCommand(params, "isDcParameterRegistered", function (e) {
        if (e.error) {
          reject(e.error);
        } else {
          resolve(!!e.i_registered);
        }
      });
    });
    if (!alreadyRegistered) {
      await new Promise(async (resolve, reject) => {
        this.instance.sendHttpCommand(params, "registerDcParameter", function (e) {
          if (e.error) {
            reject(e.error);
          } else {
            resolve();
          }
        });
      });
    }
  }
  /**
   * Open a new session with DmpConnect server.
   *
   * The Proxy and NTP settings are retrieved from the given HealthCentre.
   */
  openSession(healthCentreConfig, windowsUser) {
    return new Promise(async (resolve, reject) => {
      // If there is already an opened session, close it
      if (await this.sessionOpened()) {
        await this.closeSession();
      }
      this.cachedNetworkParams = this.networkParams(healthCentreConfig, windowsUser);
      const resultCallback = result => {
        if (result.error) {
          console.error(result.error);
          reject(new CnamApiError(`Cannot open a new session (hl_openSession)`, result.error));
        } else {
          resolve(result);
        }
      };
      this.instance.hl_openSessionWithCustomDcParam(this._cachedDcParameters, null, Object.assign(this.cachedNetworkParams, {
        configuration: await this._downloadConfiguration()
      }), resultCallback);
    });
  }
  networkParams(healthCentreConfig, windowsUser) {
    const params = {};
    if (healthCentreConfig) {
      if (healthCentreConfig.proxyAddress) {
        const proxyAddress = healthCentreConfig.proxyAddress.split(":");
        const proxyHost = proxyAddress[0];
        const proxyPort = proxyAddress[1];
        params.s_proxyServer = proxyHost;
        params.i_proxyPort = +proxyPort;
      }
      params.s_proxyLogin = healthCentreConfig.proxyLogin;
      params.s_proxyPass = healthCentreConfig.proxyPassword;
      params.s_ntpServer = healthCentreConfig.ntpAddress;
      if (params.s_ntpServer) {
        params.i_ntpPort = healthCentreConfig.ntpPort || 123;
      }
      if (healthCentreConfig.isTseEnv) {
        if (windowsUser) {
          params.s_tseCredentials = windowsUser;
        } else {
          console.warn("HealthCentre is flagged has being TSE but no Windows user has been given. Please check call to networkParams.");
        }
      }
    }
    return params;
  }
  closeSession() {
    return new Promise(async resolve => {
      // If the session is not opened, do not close it
      if (!(await this.sessionOpened())) {
        resolve();
      }
      this.instance.hl_closeSession(function (result) {
        if (result.error) {
          console.error(`Cannot close session (hl_closeSession) : ${result.error.s_apiErrorDescription}`);
          resolve();
        } else {
          resolve(result);
        }
      });
    });
  }
  async createConnector(cpsPracticeLocationIndice, practiceLocations) {
    this.pinCodeManager.refreshCacheDate();
    let func;
    if (this.isRemoteMode) {
      func = this._createRemoteDMPConnector.bind(this);
    } else {
      func = this._createDMPConnector.bind(this);
    }
    await func(
    // The list of possible value are grouped under the guid 1.2.250.1.213.1.1.4.9
    // AMBULATOIRE is the one that is the most current use case for the usage of our app.
    // We don't need to ask it to the Medic.
    "AMBULATOIRE", cpsPracticeLocationIndice, practiceLocations);
  }
  getWebPsDmpUrls(nir) {
    return new Promise((resolve, reject) => {
      return this.instance.hl_getWebPsDmpUrls(nir, 0, result => {
        if (result.error) {
          reject(new CnamApiError("Cannot get Web PS URLs", result.error));
        } else {
          resolve({
            medicDashboardURL: result.Urls[0],
            patientFolderURL: result.Urls[1],
            patientDMPManagementURL: result.Urls[2],
            documentsURL: result.Urls[3],
            carePathwayURL: result.Urls[4],
            historyAccessURL: result.Urls[5],
            parametersURL: result.Urls[6],
            rightsAndWillsURL: result.Urls[7]
          });
        }
      });
    });
  }
  getDocumentContentByUniqueId(nir, uniqueId, uuid, parameters) {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_getDocumentContentByUniqueId(nir, uniqueId, uuid, parameters, result => {
        if (result.error) {
          reject(new CnamApiError(`Cannot find documents (hl_getDocumentContent) `, result.error));
          return;
        }
        resolve(result);
      });
    });
  }
  findDocuments(nir, dmpConnectJSCriteria) {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_findDocuments(nir, dmpConnectJSCriteria, function (result) {
        if (result.error) {
          reject(new CnamApiError(`Cannot find documents (hl_findDocuments) `, result.error));
          return;
        }
        resolve(result.Documents);
      });
    });
  }
  updateDmpAccessAuthorization(nir) {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_updateUserDmpAccessAuthorization(nir, DMPConnect.UserAuthorizationAction.AddAuthorization, 0, null, function (result) {
        if (result.error) {
          reject(new CnamApiError(`Cannot authorize current medic (hl_updateUserDmpAccessAuthorization)`, result.error));
        } else {
          resolve();
        }
      });
    });
  }
  getDirectAuthenticationDMPStatus(nir) {
    return new Promise(async (resolve, reject) => {
      this.instance.hl_getDirectAuthenticationDMPStatus(nir, function (result) {
        if (result.error) {
          reject(new CnamApiError(`Cannot get authentication (hl_getDirectAuthenticationDMPStatus) `, result.error));
          return;
        }

        // First step, check the DMP status (whether it exists and is opened or not)
        const dmpStatus = result.ExistingTestAnswer.i_dmpStatus;
        switch (dmpStatus) {
          case DMPConnect.DMPStatus.DMPIsClosed:
            reject(new CnamApiDmpStatusClosed(`The DMP has been closed and must be re-opened (hl_getDirectAuthenticationDMPStatus)`, result.error));
            break;
          case DMPConnect.DMPStatus.DMPNotFound:
            reject(new CnamApiDmpStatusNotFound(`No DMP found for this patient (hl_getDirectAuthenticationDMPStatus)`, result.error));
            break;
          case DMPConnect.DMPStatus.DMPError:
            reject(new CnamApiError(`DMP Error occured (hl_getDirectAuthenticationDMPStatus)`, result.error));
            break;
          case DMPConnect.DMPStatus.DMPExist:
            // The patient has a DMP opened and working.
            break;
        }

        // Second step: check that the medic has an authorization to consult it.
        const userAuthorizationStatus = result.ExistingTestAnswer.i_userAuthorization;
        switch (userAuthorizationStatus) {
          case DMPConnect.UserAuthorizationStatus.AuthorizationExist:
            resolve({
              authorizationStatus: DmpAuthorizationStatus.Authorized
            });
            break;
          case DMPConnect.UserAuthorizationStatus.AuthorizationExpired:
            resolve({
              authorizationStatus: DmpAuthorizationStatus.Expired
            });
            break;
          case DMPConnect.UserAuthorizationStatus.NoAuthorization:
            resolve({
              authorizationStatus: DmpAuthorizationStatus.NotAuthorized
            });
            break;
          case DMPConnect.UserAuthorizationStatus.AuthorizationDenied:
            reject(new CnamApiDmpAuthorizationDenied(`The medic is blacklisted for this DMP (hl_getDirectAuthenticationDMPStatus)`, result.error));
            break;
          case DMPConnect.UserAuthorizationStatus.AuthorizationError:
            // Means that the DMP does not exists or was closed.
            // But we should never get there since there is a previous check on the DMP Status
            reject(new CnamApiError(`Fatal error - this should never happens (hl_getDirectAuthenticationDMPStatus) `, result.error));
            break;
        }
      });
    });
  }
  async createInsiConnector() {
    if (this.isRemoteMode) {
      return this._createRemoteInsiConnector();
    }
    return this._createInsiConnector();
  }
  _createRemoteInsiConnector() {
    return new Promise(async (resolve, reject) => {
      const transactionsTimeoutInSeconds = 2;
      this.instance.hl_createInsiConnector(this.connectionParams.tlsiServerFullUrl, transactionsTimeoutInSeconds,
      // FIXME these parameters should be given to the method.
      // To be updated before pushing in production.
      "AMBULATOIRE", 0, {}, res => {
        if (res.error) {
          reject(new CnamApiError(`Cannot create an INSi connector (hl_createInsiConnector) : ${res.error.s_apiErrorDescription}`, res.error));
        } else {
          resolve(res);
        }
      });
    });
  }
  _createInsiConnector() {
    return new Promise(async (resolve, reject) => {
      const transactionsTimeoutInSeconds = 2;
      this.instance.hl_createInsiConnector(this.pinCodeManager.pinCode, this.connectionParams.tlsiServerFullUrl, transactionsTimeoutInSeconds,
      // FIXME these parameters should be given to the method.
      // To be updated before pushing in production.
      "AMBULATOIRE", 0, {}, res => {
        if (res.error) {
          reject(new CnamApiError(`Cannot create an INSi connector (hl_createInsiConnector) : ${res.error.s_apiErrorDescription}`, res.error));
        } else {
          resolve(res);
        }
      });
    });
  }

  /**
   * Input requirements:
   *
   * The input strings of this transaction must:
   * - only contains capitals letters ;
   * - not contain symbols (no accent, no cedilla) ;
   * - only contains hyphen ('-') and space (' ') as special characters, and hyphen and space cannot be doubled.
   *
   * The sex can not be "unknown".
   *
   * The expected date format is YYYY-MM-DD.
   *
   * The birthplace format is COG (Code Officiel Géographique, @see https://www.insee.fr/fr/information/2560452 )
   */
  getInsFromIdentityInformation(billingNumber, name, given, sex, date, place) {
    const idamDevelopmentIcanopee = "NSC17HE20GM0";
    const idamProductionSemeia = "PLT17NF22FM1";
    // const lpsInfos = {
    //   s_idam: "FFC00NF12HW0", // Software authorization number. From the CNDA. (example value from examples_tlsi.html)
    //   s_numAM: "04", // IDAM type. Currently must be set to "04" (Authorization number).
    //   s_version: "1", // Software version. No specific format. E.g. "01.00", or "1.0", or "2".
    //   s_instance: "550e8400-e29b-41d4-a716-446655440000", //"550e8400-e29b-41d4-a716-446655440000", // Software instance UUID in its canonical form.
    //   s_name: "Semeia - Medics Web", //Semeia - Medics Web", // The software name.
    //   s_billingNumber: "" // Billing situation. Either FINESS for institutions or AM number in all other cases. Mandatory for INSi.
    // };

    const lpsInfoICanopee = {
      s_idam: this.isDevelopmentMode ? idamDevelopmentIcanopee : idamProductionSemeia,
      // Software authorization number. From the CNDA. (example value from examples_tlsi.html)
      s_numAM: "4",
      // IDAM type. Currently must be set to "04" (Authorization number).
      s_version: "1",
      // Software version. No specific format. E.g. "01.00", or "1.0", or "2".
      s_instance: "550e8400-e29b-41d4-a716-446655440000",
      //"550e8400-e29b-41d4-a716-446655440000", // Software instance UUID in its canonical form.
      s_name: "Pilot",
      //Semeia - Medics Web", // The software name.
      s_billingNumber: billingNumber // Billing situation. Either FINESS for institutions or AM number in all other cases. Mandatory for INSi.
    };
    return new Promise(async (resolve, reject) => {
      this.instance.hl_getInsFromIdentityInformation(lpsInfoICanopee, name, given, sex, date, place, res => {
        if (this.isDevelopmentMode && res.Identity) {
          // The INS service returns a "production" OID, while a test or demo one is expected for calling the DMP afterwards.
          // Forcing to test or demo so that the full INS / DMP cycle can be tested.
          if (res.Identity.Ins.s_value == "2550699999999") {
            // This is Nathalie Desmaux, which is a demo patient
            // However, Nathalie Desmaux does not currently exist in the devh environment.
            res.Identity.Ins.s_oid = InsOid.DEMO;
          } else {
            res.Identity.Ins.s_oid = InsOid.TEST;
          }
        }
        if (res.error) {
          console.log(res.error);
          reject(new CnamApiInsError(`Cannot get the patient INS from identity information (hl_getInsFromIdentityInformation): ${res.error.s_apiErrorTlsiErrorDetailError}`, res.error));
        }
        resolve(res);
      });
    });
  }
  async _readRemoteCPSCard() {
    return new Promise(async (resolve, reject) => {
      await this.instance.hl_getCpxCard(0, "WIRELESS CPX", result => {
        if (result.error) {
          reject(new CnamApiCpsCardError(`Cannot get CPS card content : ${result.error.s_apiErrorDescription}`, result.error));
          return;
        }
      });
      return this.instance.hl_readCpxCard(result => {
        if (result.error) {
          if (result.error.i_apiErrorCode === 8) {
            reject(new CnamApiCpsCardBlocked(`Cannot read CPS card content (CPS card is blocked)`, result.error));
          } else if (result.error.i_apiErrorCode === 7) {
            reject(new CnamApiCpsCardWrongCode(`Cannot read CPS card content (Wrong pin code)`, result.error));
          } else {
            reject(new CnamApiError(`Cannot get CPS card content : ${result.error.s_apiErrorDescription} - ${result.error.s_apiErrorExtendedInformations}`, result.error));
          }
        } else {
          resolve(result);
        }
      });
    });
  }
  async _readCPSCard() {
    const pinCode = this.pinCodeManager.pinCode || this.pinCodeManager.typedPinCode;
    return new Promise(async (resolve, reject) => {
      try {
        const cards = await this.detectCards();
        if (!cards.cps) {
          reject(new CnamApiCpsCardError(`CPS card is not detected`));
          return;
        }
        this.instance.hl_getCpxCard(cards.cps.index, cards.cps.name, result => {
          if (result.error) {
            reject(new CnamApiCpsCardError(`Cannot get CPS card content : ${result.error.s_apiErrorDescription}`, result.error));
            return;
          }
          this.instance.hl_readCpxCard(pinCode, result => {
            if (result.error) {
              if (result.error.i_apiErrorCode === 8) {
                reject(new CnamApiCpsCardBlocked(`Cannot read CPS card content (CPS card is blocked)`, result.error));
              } else if (result.error.i_apiErrorCode === 7) {
                reject(new CnamApiCpsCardWrongCode(`Cannot read CPS card content (Wrong pin code)`, result.error));
              } else {
                reject(new CnamApiCpsCardError(`Cannot get CPS card content : ${result.error.s_apiErrorDescription}`, result.error));
              }
            } else {
              this.pinCodeManager.cachePinCode(pinCode);
              this.pinCodeManager.typedPinCode = "";
              resolve(result);
            }
          });
        });
      } catch (e) {
        reject(e);
      }
    });
  }
  async readCPSCard() {
    if (this.isRemoteMode) {
      return this._readRemoteCPSCard();
    }
    return this._readCPSCard();
  }
  readVitaleCard() {
    return new Promise(async (resolve, reject) => {
      const cards = await this.detectCards();
      if (!cards.vitale) {
        reject(new CnamApiVitaleCardError("Vitale card is not detected"));
        return;
      }
      this.instance.hl_getVitaleCard(cards.vitale.index, cards.vitale.name, result => {
        if (result.error) {
          reject(new CnamApiVitaleCardError(`Cannot get vitale card content : ${result.error.s_apiErrorDescription}`, result.error));
          return;
        }
        this.instance.hl_readVitaleCard(result => {
          if (result.error) {
            reject(new CnamApiVitaleCardError(`Cannot read vitale card content : ${result.error.s_apiErrorDescription}`, result.error));
            return;
          }
          resolve(result);
        });
      });
    });
  }

  /**
   *  This method retrieves connected Smart Cards and create a containing with
   *  the connected cards.
   *
   *  The hash categorizes the cards as CPS or Vitale cards.
   *
   *  TODO: observe the cards and update this _cards list through
   *  - hl_startVitaleCardMonitoring
   *  - stopVitaleCardMonitoring
   */
  detectCards() {
    return new Promise((resolve, reject) => {
      this.instance.hl_getPcscReaders(function (result) {
        if (result.error) {
          reject(new CnamApiError(`Cannot get Pcs Readers : ${result.error.s_apiErrorDescription}`, result.error));
          return;
        }

        // The code currently considers there can be only one CPS card and one Vitale card.
        const cards = {};
        for (let i = 0; i < result.Readers.length; i++) {
          const reader = result.Readers[i];
          switch (reader.i_slotType) {
            case 2:
              cards.vitale = {
                index: i,
                name: reader.s_name
              };
              break;
            case 3:
              cards.cps = {
                index: i,
                name: reader.s_name
              };
              break;
          }
        }
        resolve(cards);
      });
    });
  }
  async _createRemoteDMPConnector(practiceSetting, cpsPracticeLocationIndice, practiceLocations) {
    return new Promise(async (resolve, reject) => {
      const networkParams = this._networkParamsForDmp();
      // Setting a custom PracticeLocation is only available from the 1.6.2 version
      const practiceLocation = this.medic?.forceFinessForCnamRetrieval && practiceLocations ? {
        s_practiceLocationStructureId: this.medic.forceFinessForCnamRetrieval,
        s_practiceLocationName: practiceLocations[cpsPracticeLocationIndice].structureName,
        s_practiceLocationActivitySector: practiceLocations[cpsPracticeLocationIndice].activity,
        s_practiceLocationPracticeSettings: "AMBULATOIRE"
      } : undefined;
      const optionalParameters = {
        ...networkParams,
        PracticeLocation: practiceLocation
      };
      this.instance.hl_createDmpConnector(this.connectionParams.dmpServerName, this.transactionsTimeoutInSeconds, practiceSetting, cpsPracticeLocationIndice, optionalParameters, result => {
        if (result.error) {
          reject(new CnamApiError(`Cannot create a connector (hl_createDmpConnector) : ${result.error.s_apiErrorDescription}`, result.error));
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Create a connector to request the DMP.
   */
  _createDMPConnector(practiceSetting, cpsPracticeLocationIndice, practiceLocations) {
    return new Promise((resolve, reject) => {
      const networkParams = this._networkParamsForDmp();
      // Setting a custom PracticeLocation is only available from the 1.6.2 version
      const practiceLocation = this.medic?.forceFinessForCnamRetrieval && practiceLocations ? {
        s_practiceLocationStructureId: this.medic.forceFinessForCnamRetrieval,
        s_practiceLocationName: practiceLocations[cpsPracticeLocationIndice].structureName,
        s_practiceLocationActivitySector: practiceLocations[cpsPracticeLocationIndice].activity,
        s_practiceLocationPracticeSettings: "AMBULATOIRE"
      } : undefined;
      const optionalParameters = {
        ...networkParams,
        PracticeLocation: practiceLocation
      };
      this.instance.hl_createDmpConnector(this.pinCodeManager.pinCode, this.connectionParams.dmpServerName, this.transactionsTimeoutInSeconds, practiceSetting, cpsPracticeLocationIndice, optionalParameters, result => {
        if (result.error) {
          reject(new CnamApiError(`Cannot create a connector (hl_createDmpConnector) : ${result.error.s_apiErrorDescription}`, result.error));
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Returns the Network params for the createDmpConnector method.
   * This is based on the DmpConnect params but some attributes have a different name,
   * thus they are renamed.
   */
  _networkParamsForDmp() {
    const result = {};
    const dmpConnectParams = this.cachedNetworkParams;
    for (const key in dmpConnectParams) {
      if (key == "s_proxyServer") {
        result.s_proxyIpOrUrl = dmpConnectParams.s_proxyServer;
      } else if (key == "s_ntpServer") {
        result.s_ntpIpOrURl = dmpConnectParams.s_ntpServer;
      } else if (["i_proxyPort", "s_proxyLogin", "s_proxyPass", "i_ntpPort"].includes(key)) {
        result[key] = dmpConnectParams[key];
      }
    }
    return result;
  }

  /**
   * Set the confidentiality level (default to false)
   * If true, it means that that following transactions will not be recorded in logs
   */
  setConfidentialityLevel(enableSecretConnection) {
    return new Promise((resolve, reject) => {
      this.instance.hl_setConfidentialityLevel(enableSecretConnection ? 1 : 0, function (result) {
        if (result.error) {
          reject(new CnamApiError(`Cannot set the confidentiality level (hl_setConfidentialityLevel) : ${result.error.s_apiErrorDescription}`, result.error));
        } else {
          resolve(result);
        }
      });
    });
  }
}